import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BookableColorKey, getDefaultTaskColorKey } from '../utils/calendarColors'
import { formatTimeStamp } from '../utils/dates'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type TaskConstructorArgs = ConstructorArgs<() => Task>

export class Task extends BaseRecord implements Bookable {
    readonly recordType = 'task'

    readonly iBookable = true

    date?: moment.Moment
    memo?: string
    title?: string
    completedDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<TaskConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.title = data.title ?? undefined
        this.memo = data.memo ?? undefined
        this.date = this.propertyFromTimestampArg(data.date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<TaskConstructorArgs>): Task {
        return new Task({ ...this.serializeForClone(), ...overrides })
    }

    get statusString(): string {
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.completedDate) return `Completed ${this.completedDate.format('MMM D, YYYY')}`
        return this.person?.name ?? 'Task'
    }

    get scheduleStates(): ScheduleState[] {
        if (this.completedDate) return [ScheduleState.Complete]
        return [ScheduleState.Completable, ScheduleState.Cancelable]
    }

    get endDate(): moment.Moment | undefined {
        return this.date?.clone().add(30, 'minutes')
    }

    set endDate(endDate: moment.Moment | undefined) {
        // No operation
    }

    get confirmedDate(): moment.Moment | undefined {
        return this.createdDate
    }

    get duration(): number | undefined {
        return undefined
    }

    get location(): string | undefined {
        return undefined
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    // Bookable interface methods
    get color(): BookableColorKey {
        return getDefaultTaskColorKey()
    }

    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get allowNotify(): boolean {
        return false
    }

    get hasSchedule(): boolean {
        return false
    }

    get isRecurring(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.date) this.date = moment(data.date)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['title'] = this.title ?? ''
        dict['memo'] = this.memo ?? ''
        dict['date'] = formatTimeStamp(this.date)
        dict['person'] = this.person?.id ?? ''
        dict['employee'] = this.employee?.id ?? ''
        dict['subaccount'] = this.subaccount?.id ?? ''
        return dict
    }

    static fk: FKType = {
        users: ['owner', 'person', 'creator', 'employee'],
        subaccounts: ['subaccount'],
    }

    static dataType(): DataType {
        return 'tasks'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
}
